import { getPendingWindowsEVRequestName } from './~reusables/util/windowsEVRequest';
import React from 'react';
import { Store, useStoreState, InjectStoreState } from 'pullstate';
import isEmpty from 'lodash.isempty';
import { ITeam, IUserTeam } from './~reusables/types/types';
import { initialBuildState } from './~reusables/actions/builds';
import {
  Build,
  GlobalFeatureFlags,
  hasActiveSub,
  hasPlan,
  IApp,
  IUser,
  Plan,
  UserIHaveAcceptedInviteFrom,
  UserIHaveSentInviteTo,
  WindowsEVOnboardingProvider,
} from '@todesktop/shared';

export type InvitePermissions = NonNullable<
  UserIHaveSentInviteTo['permissions']
>;

export interface InvitedUsersState {
  accountsById: Record<string, UserIHaveSentInviteTo[]>;
  loadingAccountIds: Record<string, boolean>;
}

export interface AppUser {
  id: string;
  label: string;
  isOwner: boolean;
}

export interface IGlobalState {
  selectedApp: IApp['id'] | null;
  selectedTeam: ITeam | null; // specific team with its own app collections and so on
  userTeams: IUserTeam[]; // teams that logged in user is part of (id match)
  user: IUser | null;
  invitedUsers: InvitedUsersState;
  acceptedUsers: UserIHaveAcceptedInviteFrom[];
  /** Teams the person was removed from during this session. */
  removedTeams: { id: string; name: string }[];
  /** The team the create-app modal adds to, when not the person's own. */
  createAppAccountId: string | null;
  apps: Array<IApp | never>;
  appToUser: {
    [appId: string]: AppUser;
  };
  buildState: {
    /**
     * The newest builds (kept live), any older pages loaded with
     * `loadOlderBuilds`, and builds loaded by ID. Use `selectListedBuilds` for
     * the builds that can be listed without gaps.
     */
    builds: { [key: string]: Build };
    hasBuildsDataLoaded: boolean;
    selectedBuild: Build['id'];
    releasedBuildId: Build['id'];
    /** Whether older builds remain to be loaded with `loadOlderBuilds` */
    hasOlderBuilds: boolean;
    isLoadingOlderBuilds: boolean;
    /** `createdAt` of the oldest listed build while `hasOlderBuilds` */
    oldestListedCreatedAt: string;
    /** Number of builds the app has, once counted */
    buildCount: number | null;
    /** Builds watched by ID that don't exist */
    missingBuildIds: { [key: string]: true };
    hasRecentReleasesLoaded: boolean;
  };
  liveFields: Record<string, number | string | undefined>;
  /** Contents of globals/featureFlags; resolve flags with useFeatureFlag */
  globalFeatureFlags: GlobalFeatureFlags | null;
  globalLoading: boolean;
  /** Auth has resolved and, when signed in, the user document has loaded */
  userDataLoaded: boolean;
  /** The first read of every account's apps has finished, or failed */
  appsLoaded: boolean;
  /** Accounts (the person's own and their teams) whose apps have loaded */
  accountAppsLoaded: Record<string, true>;
  applicationLoadFailed: boolean;
  onboarded: boolean;
  shouldCreateSubWithoutBuild: boolean;
  previewScreenshotOS: 'windows' | 'mac';
  manageUsers: {
    activeAccountId: string | null;
  };
  /** Prefills the admin login dialog's email */
  adminLoginEmail?: string | null;
  uiState?:
    | 'choose-plan'
    | 'pay'
    | 'subscribe'
    | 'pay-todesktop-builder'
    | 'build'
    | 'create-sub-without-build'
    | 'reset-password-on-upgraded-account'
    | 'manage-subscriptions'
    | 'manage-billing'
    | 'create-new-app'
    | 'manage-account'
    | 'manage-security-token'
    | 'admin-login'
    | null;
}

const isWindows = window.navigator.platform.includes('Win');

export const initialState: IGlobalState = {
  selectedApp: null,
  selectedTeam: null,
  userTeams: [],
  user: null,
  invitedUsers: {
    accountsById: {},
    loadingAccountIds: {},
  },
  acceptedUsers: [],
  removedTeams: [],
  createAppAccountId: null,
  onboarded: false,
  globalFeatureFlags: null,
  globalLoading: true,
  userDataLoaded: false,
  appsLoaded: false,
  accountAppsLoaded: {},
  applicationLoadFailed: false,
  previewScreenshotOS: isWindows ? 'windows' : 'mac',
  shouldCreateSubWithoutBuild: false,
  uiState: null,
  liveFields: {},
  apps: [],
  appToUser: {},
  buildState: initialBuildState,
  manageUsers: {
    activeAccountId: null,
  },
};

export const store = new Store(initialState);

type getSubstateFn<SS> = (state: IGlobalState) => SS;
export const useStore: <SS>(getSubstate: getSubstateFn<SS>) => SS = (
  substate,
) => useStoreState(store, substate);

export const update: (updateFn: (state: IGlobalState) => void) => void = (
  updateFn,
) => store.update(updateFn);

export const subscribe: <T>(
  watchFn: (state: IGlobalState) => T,
  listener: (watched: T, allState: IGlobalState, prevWatched: T) => void,
) => () => void = (watchFn, listener) => store.subscribe(watchFn, listener);

if (process.env.NODE_ENV === 'development') {
  subscribe(
    (state: IGlobalState) => state,

    (state: IGlobalState) => state && console.info({ state }),
  );
}

interface IPropsInjectMainStore<SS = any> {
  on?: (state: IGlobalState) => SS;
  children: (output: SS) => React.ReactElement<any>;
}

export const InjectStore: React.FC<IPropsInjectMainStore> = ({
  on,
  children,
}) => (
  <InjectStoreState store={store} on={on}>
    {(injectedState) => children(injectedState)}
  </InjectStoreState>
);

// STORE REDUCERS
// TODO: Prefix selectors with $ symbol
export const selectedApp = (state: IGlobalState) =>
  selectAppFromID(state.selectedApp)(state);

export const $leaderApp = (state: IGlobalState) => {
  const app = selectedApp(state);
  return app?.parent
    ? state.apps.find(({ id }) => id === app?.parent?.id) || app
    : app;
};

export const isAppOwner = (state: IGlobalState) => {
  return selectedAppUser(state).isOwner === true;
};

export const selectedAppUser = (state: IGlobalState): AppUser => {
  const app = selectAppFromID(state.selectedApp)(state);
  return state.appToUser[app?.id as string];
};

export const selectAppFromID =
  (id?: IApp['id'] | null) => (state: IGlobalState) =>
    state.apps.find(
      (app) =>
        typeof app.id === 'string' && app.id === (id || state.selectedApp),
    );

export const getLiveField = (name: string) => (state: IGlobalState) => {
  const { liveFields } = state;
  const app = selectedApp(state);
  if (app) {
    const { id } = app;
    return liveFields[`${id}-${name}`];
  }
};

export const appHasActiveSubscription = (state: IGlobalState): boolean => {
  return hasActiveSub($leaderApp(state)?.subscription);
};

export const appHasOwnActiveSubscription = (state: IGlobalState): boolean => {
  return hasActiveSub(selectedApp(state)?.subscription);
};

export const $completedChecklistItems = (state: IGlobalState) => {
  const app = selectedApp(state);
  return app && app.meta && app.meta.completedChecklistItems
    ? app.meta.completedChecklistItems
    : {};
};

export const $completedEVOnboardingSteps = (state: IGlobalState) => {
  const app = selectedApp(state);
  const onboarding = app?.meta?.windowsEVOnboarding;
  const pendingName = getPendingWindowsEVRequestName(app);
  const hasUploadedCert = !!onboarding?.tempHSMCertName && !pendingName;
  return {
    ...onboarding,
    hasGeneratedCert: !!pendingName || hasUploadedCert,
    hasUploadedCert,
  };
};

export const $hasPlanAccess = (plan: Plan) => (state: IGlobalState) => {
  const app = $leaderApp(state);
  return hasPlan(plan, app?.subscription);
};

const EVProviderFormatting: {
  [name in WindowsEVOnboardingProvider]: string;
} = {
  globalsign: 'GlobalSign',
  other: 'Other',
};

export const $windowsEVOnboardingProvider = (
  state: IGlobalState,
): { provider: WindowsEVOnboardingProvider; providerFormatted: string } => {
  const app = selectedApp(state);
  const provider =
    app &&
    app.meta &&
    app.meta.windowsEVOnboarding &&
    app.meta.windowsEVOnboarding.provider
      ? app.meta.windowsEVOnboarding.provider
      : WindowsEVOnboardingProvider.globalsign;

  return {
    provider,
    providerFormatted: EVProviderFormatting[provider],
  };
};

export const $macCertExists = (state: IGlobalState) => {
  const app = selectedApp(state);
  if (!app) return false;

  const { customMacCodeSign, macHsmCertNames } = app;

  const hasHSMCert = Boolean(macHsmCertNames?.mac);
  const hasFallbackCert = customMacCodeSign && !isEmpty(customMacCodeSign);
  return hasHSMCert || hasFallbackCert;
};

export const $windowsCertExists = (state: IGlobalState) => {
  const app = selectedApp(state);
  if (!app) return false;

  const { customWindowsCodeSign, windowsHsmCertNames } = app;
  const hasFallbackCert =
    customWindowsCodeSign &&
    customWindowsCodeSign.certType &&
    'hsmCertName' in customWindowsCodeSign &&
    'hsmCertType' in customWindowsCodeSign;

  const hasHSMCert = Boolean(windowsHsmCertNames?.windows);

  const hasAzureCert = Boolean(
    customWindowsCodeSign?.certType === 'azureTrustedSigning',
  );
  return hasHSMCert || hasFallbackCert || hasAzureCert;
};

export const $linuxCertExists = (state: IGlobalState) => {
  const app = selectedApp(state);
  if (!app) return false;

  const { customLinuxPgpKey } = app;
  return customLinuxPgpKey?.$key !== '';
};

export const $tempHSMCertName = (state: IGlobalState) => {
  return getPendingWindowsEVRequestName(selectedApp(state));
};

export const $shouldMinimizeOnboarding = (state: IGlobalState) => {
  const app = selectedApp(state);

  if (app && app.meta && app.meta.completedChecklistItems) {
    return !!app.meta.completedChecklistItems.shouldMinimizeOnboarding;
  }

  return false;
};

export const appIsBuilding = (state: IGlobalState): boolean => {
  const app = selectedApp(state);
  if (!app) {
    return false;
  }
  const { meta } = app;
  const currentBuildProgress = meta && (meta.currentBuildProgress as any);
  if (!currentBuildProgress) {
    return false;
  }
  if (currentBuildProgress.mac && currentBuildProgress.mac.isBuilding) {
    return true;
  }
  if (currentBuildProgress.windows && currentBuildProgress.windows.isBuilding) {
    return true;
  }
  if (currentBuildProgress.linux && currentBuildProgress.linux.isBuilding) {
    return true;
  }
  return false;
};

// Global loading helpers
export const setGlobalLoading = (loading: boolean) => {
  update((state) => {
    state.globalLoading = loading;
  });
};

export const $globalLoading = (state: IGlobalState) => state.globalLoading;
